import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateHousehold, useAcceptInvite } from '../hooks/useHousehold';
import { useAuthContext } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';

type Mode = 'choose' | 'create' | 'join';

export function OnboardingScreen() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { displayName } = useAuthContext();
  const [mode, setMode] = useState<Mode>('choose');

  const [householdName, setHouseholdName] = useState('');
  const [userName, setUserName] = useState(displayName);
  const [inviteToken, setInviteToken] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useCreateHousehold();
  const acceptMutation = useAcceptInvite();

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!householdName.trim()) newErrors.householdName = 'Informe o nome da lista';
    if (!userName.trim()) newErrors.userName = 'Informe seu nome';
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    try {
      await createMutation.mutateAsync({ householdName: householdName.trim(), userName: userName.trim() });
      navigate('/items');
    } catch {
      addToast('Erro ao criar lista. Tente novamente.', 'error');
    }
  };

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteToken.trim()) { setErrors({ inviteToken: 'Informe o código de convite' }); return; }
    try {
      await acceptMutation.mutateAsync({ token: inviteToken.trim() });
      navigate('/items');
    } catch {
      addToast('Código inválido ou expirado.', 'error');
    }
  };

  if (mode === 'create') {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <div className="bg-gradient-to-b from-emerald-600 to-emerald-500 px-6 pt-12 pb-10 text-white">
          <button onClick={() => setMode('choose')} className="mb-4 text-emerald-100 text-sm font-medium flex items-center gap-1 touch-manipulation">
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Voltar
          </button>
          <h1 className="text-2xl font-bold">Criar minha lista</h1>
          <p className="text-emerald-100 mt-1 text-sm">Configure seu espaço no HomeStock</p>
        </div>
        <div className="flex-1 px-5 -mt-4">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
            <form onSubmit={handleCreate} className="space-y-4">
              <Input
                label="Nome da lista / família"
                placeholder="ex: Casa dos Oliveira"
                value={householdName}
                onChange={(e) => setHouseholdName(e.target.value)}
                error={errors.householdName}
                name="householdName"
                autoFocus
              />
              <Input
                label="Seu nome"
                placeholder="ex: Maria"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                error={errors.userName}
                name="userName"
              />
              <Button
                type="submit"
                variant="primary"
                fullWidth
                size="lg"
                loading={createMutation.isPending}
              >
                Criar lista
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'join') {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <div className="bg-gradient-to-b from-emerald-600 to-emerald-500 px-6 pt-12 pb-10 text-white">
          <button onClick={() => setMode('choose')} className="mb-4 text-emerald-100 text-sm font-medium flex items-center gap-1 touch-manipulation">
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Voltar
          </button>
          <h1 className="text-2xl font-bold">Entrar em uma lista</h1>
          <p className="text-emerald-100 mt-1 text-sm">Use o código de convite recebido</p>
        </div>
        <div className="flex-1 px-5 -mt-4">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
            <form onSubmit={handleJoin} className="space-y-4">
              <Input
                label="Código de convite"
                placeholder="Cole o código aqui"
                value={inviteToken}
                onChange={(e) => setInviteToken(e.target.value)}
                error={errors.inviteToken}
                name="inviteToken"
                autoFocus
              />
              <p className="text-xs text-gray-500">
                Peça para quem te convidou gerar um link de convite em Configurações.
              </p>
              <Button
                type="submit"
                variant="primary"
                fullWidth
                size="lg"
                loading={acceptMutation.isPending}
              >
                Entrar na lista
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="bg-gradient-to-b from-emerald-600 to-emerald-500 px-6 pt-16 pb-12 flex flex-col items-center text-white">
        <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mb-5">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold">Bem-vindo!</h1>
        <p className="mt-2 text-emerald-100 text-center">Como deseja começar?</p>
      </div>

      <div className="flex-1 px-5 -mt-6 space-y-4">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 space-y-4">
          <button
            onClick={() => setMode('create')}
            className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-emerald-500 bg-emerald-50 text-left touch-manipulation hover:bg-emerald-100 transition-colors"
          >
            <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-gray-900">Criar minha lista</p>
              <p className="text-sm text-gray-500 mt-0.5">Começo do zero, crio meu espaço</p>
            </div>
          </button>

          <button
            onClick={() => setMode('join')}
            className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-200 bg-white text-left touch-manipulation hover:bg-gray-50 transition-colors"
          >
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-gray-900">Entrar em lista existente</p>
              <p className="text-sm text-gray-500 mt-0.5">Já tenho um convite de alguém</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
